import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import PlatFormAccordionTitle from "./PlatFormAccordionTitle";
import PlatFormUserInfoContent from "./PlatFormUserInfoContent";
import PlatFormAudioTrackTitle from "./PlatFormAudioTrackTitle";
import PlatformTrackInfo from "./PlatFormTrackinfo";
import PlatformCountries from "./PlatformCountries";
import FinalPricing from "./FinalPricing";
import ReleaseNoticeModal from "./ReleaseNoticeModal";
import ThemeStore from "stories/ThemeStore";
import BackArrow from "assets/back-arrow.svg";
import { Typography } from "ui-kit/Typography";
import {
    Accordion,
    AccordionItem,
    AccordionItemHeading,
    AccordionItemButton,
    AccordionItemPanel,
} from 'react-accessible-accordion';
import TopVector from "assets/top-vector.svg";
import BottomVector from "assets/bottom-vector.svg";
import { PlatformReleaseInfoProps, Track } from "../types";
import { useNavigate, useParams } from "react-router";
import { ProgressSpinner } from "primereact/progressspinner";
import IntlStore from "stories/IntlStore";
import { useRelease } from "components/NewRelease/hooks/useRelease";
import { useLanguages } from "components/NewRelease/hooks/useLanguages";
import { runDraftSaveIfNeeded } from "components/NewRelease/draftSaveGuard";

export const PlatformReleaseInfo: React.FC<PlatformReleaseInfoProps> = ({ releases, audio, loading }) => {

    const params = useParams();
    const id = params.id;
    const [expandedItems, setExpandedItems] = useState<string[]>(['release-info', 'platforms', 'pricing']);
    const [step, setStep] = useState<number>(1)
    const theme = ThemeStore.activeTheme;
    const tracks = releases.releases.data?.tracks || [];
    const paid = !!releases.releases.data?.is_paid;
    const [deliveryId, setDeliveryId] = useState<number>(1)
    const { languages } = useLanguages() as { languages: any[] };

    const { submitRelease, release: releaseMeta, fetchRelease } = useRelease();

    useEffect(() => {
        if (!id) return;
        // Hydrate shared release meta (cover/title) from cache/backend
        fetchRelease({ backgroundRefresh: true }).catch(() => {});
    }, [id, fetchRelease]);

    const releaseType = releases.releases.data?.release_type;
    const hasUgc = releases.releases.data?.platforms?.some(
        (p: any) => p.ugc
    ) || false;


    const location = useLocation();
    const navigate = useNavigate();

    const onBack = () => {
        (async () => {
          try {
            await runDraftSaveIfNeeded();
        navigate(`/releases/new-release/${id}?step=publish`);
          } catch (err) {
            console.error("[Review] draft save failed; navigation cancelled", err);
          }
        })();
    };

    const onEditReleaseInfo = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/releases/new-release/${id}?step=release-info`);
    };

    const handlePublish = async () => {
        if (!id) return;
        submitRelease(Number(id)).then(() => {
            navigate('/releases')
        })
    };

    React.useEffect(() => {
        const hash = location.hash;

        if (!hash) return;

        let targetUuid = '';
        switch (hash) {
            case '#release-information':
                targetUuid = 'release-info';
                break;
            case '#platform-and-countries':
                targetUuid = 'platforms';
                break;
            case '#final-pricing':
                targetUuid = 'pricing';
                break;
            default:
                return;
        }


        setExpandedItems(prev => {
            if (!prev.includes(targetUuid)) {
                return [...prev, targetUuid];
            }
            return prev;
        });


        const el = document.getElementById(targetUuid);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [location.hash]);


    const handleChange = (uuids: string[]) => {
        setExpandedItems(uuids);
    };

    // Hide Publish block if release is already submitted/released
    const releaseStatusRaw =
        (releaseMeta as any)?.status ??
        (releases as any)?.releases?.data?.status ??
        "";
    const releaseStatus = String(releaseStatusRaw || "").toLowerCase();
    const isAlreadySubmitted =
        releaseStatus !== "" && releaseStatus !== "draft";

    return (

        <>
            
            <Accordion
                allowZeroExpanded
                allowMultipleExpanded
                preExpanded={expandedItems}
                onChange={handleChange}>
                <AccordionWrapper>
                    
                    <AccordionItem
                        uuid={'release-info'}
                        key={'release-info'}
                        id="release-info">
                        <AccordionItemHeading>
                       
                            <StyledAccordionItemButton
                                style={{ borderRadius: expandedItems.includes('release-info') ? '25px 25px 0 0' : '25px' }}>
                                <ReleaseInfoHeader>
                                    <ReviewNavRow>
                                        <BackLink
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onBack();
                                            }}
                                        >
                                            <BackArrow color={theme.palette.text.breadcrumbs} />
                                            <BackText>
                                                <FormattedMessage
                                                    id="NewRelease.Platform.Breadcrumbs"
                                                    defaultMessage="Platform and countries"
                                                />
                                            </BackText>
                                        </BackLink>
                                        <ReviewTitle variant="subtitle2">
                                            <FormattedMessage
                                                id="NewRelease.Platform.Title"
                                                defaultMessage="Review (Step 7/7)"
                                            />
                                        </ReviewTitle>
                                    </ReviewNavRow>

                                    <ReleaseInfoRow>
                                        <ReleaseInfoTitleAndEdit>
                                            <ReleaseInfoTitleText>
                                                {IntlStore.intl.formatMessage({
                                        id: "NewRelease.Platform.ReleaseInformation",
                                        defaultMessage: "Release information"
                                    })}
                                            </ReleaseInfoTitleText>
                                            {expandedItems.includes('release-info') && (
                                                <ReleaseInfoEditButton onClick={onEditReleaseInfo}>
                                                    <FormattedMessage
                                                        id="NewRelease.Platform.Edit"
                                                        defaultMessage="Edit"
                                                    />
                                                </ReleaseInfoEditButton>
                                            )}
                                        </ReleaseInfoTitleAndEdit>
                                {
                                    expandedItems.includes('release-info')
                                        ? <BottomVector color={theme.palette.text.breadcrumbs} />
                                        : <TopVector color={theme.palette.text.breadcrumbs} />
                                }
                                    </ReleaseInfoRow>
                                </ReleaseInfoHeader>
                            </StyledAccordionItemButton>
                        </AccordionItemHeading>
                        <StyledAccordionItemPanel
                            style={{ borderRadius: expandedItems.includes('release-info') ? '0 0 25px 25px' : '25px' }}>
                            <PlatFormUserInfoContent
                                releases={releases}
                                link={`/releases/new-release/${id}?step=upload-image`}
                                step={step}
                                setStep={setStep}
                                preview={(releaseMeta as any)?.cover_url ?? null}
                            />
                        </StyledAccordionItemPanel>
                    </AccordionItem>
                    <AccordionSection style={{ height: loading ? '125px' : "auto" }}>
                        {loading ? (
                            <Loading>
                                <ProgressSpinner />
                            </Loading>
                        ) : (
                          <>
                            {/* Track information section header (should appear only once) */}
                            {tracks.length > 0 && (
                              <TrackInfoHeaderRow style={{ padding: "0 18px 10px" }}>
                                <TrackInfoTitleAndEdit>
                                  <TrackInfoTitle>
                                    <FormattedMessage
                                      id="NewRelease.Platform.TrackInformation"
                                      defaultMessage="Track information"
                                    />
                                  </TrackInfoTitle>
                                  <TrackInfoEditButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/releases/new-release/${id}?step=track-upload`);
                                    }}
                                  >
                                    <FormattedMessage
                                      id="NewRelease.Platform.Edit"
                                      defaultMessage="Edit"
                                    />
                                  </TrackInfoEditButton>
                                </TrackInfoTitleAndEdit>
                              </TrackInfoHeaderRow>
                            )}

                            {tracks.map((track: Track, index: number,) => {
                            const uuid = `track-${track.id}`;
                            const isExpanded = expandedItems.includes(uuid);
                            const trackArtistsText =
                                Array.isArray(track.artists) && track.artists.length
                                    ? track.artists.map(a => a.name).filter(Boolean).join(", ")
                                    : "";
                            const langMatch = Array.isArray(languages)
                              ? languages.find((l: any) =>
                                  String(l?.id ?? "") === String(track.metadata_language) ||
                                  String(l?.code ?? "") === String(track.metadata_language) ||
                                  String(l?.language_id ?? "") === String(track.metadata_language)
                                )
                              : undefined;
                            const trackLanguageText =
                              (langMatch?.language ?? langMatch?.name) || (track.metadata_language ? String(track.metadata_language) : "");
                            return (
                                <>
                                    <AccordionItem uuid={uuid} key={track.id}>
                                        <AccordionItemHeading>
                                            <StyledAccordionItemButton
                                                style={{ borderRadius: isExpanded ? '25px 25px 0 0' : '25px', }}>
                                                <TrackButtonLeft>
                                                <PlatFormAudioTrackTitle
                                                    index={index + 1}
                                                    title={track.title}
                                                    artists={trackArtistsText}
                                                    audio={audio}
                                                    id={track.id} />
                                                </TrackButtonLeft>
                                                {isExpanded ? (
                                                    <BottomVector color={theme.palette.text.breadcrumbs} />
                                                ) : (
                                                    <TopVector color={theme.palette.text.breadcrumbs} />
                                                )}
                                            </StyledAccordionItemButton>
                                        </AccordionItemHeading>
                                        <StyledAccordionItemPanel
                                            style={{ borderRadius: isExpanded ? '0 0 25px 25px' : '25px', }}>
                                            <PlatformTrackInfo
                                                title={track.title}
                                                version={track.version}
                                                language={trackLanguageText}
                                                releases={releases}
                                                audio={audio}
                                                id={track.id}
                                                isrc={track.isrc}
                                                artists={track.artists}
                                                link={`/releases/new-release/${id}?step=track-upload&trackId=${track.id}`} />
                                        </StyledAccordionItemPanel>
                                    </AccordionItem>
                                    {index !== tracks.length - 1 && <LineBox><Line /></LineBox>}
                                </>
                            );
                        })}
                          </>
                        )}
                    </AccordionSection>
                    <AccordionItem uuid={'platforms'} key={'platforms'} id="platforms">
                        <AccordionItemHeading>
                            <StyledAccordionItemButton
                                style={{ borderRadius: expandedItems.includes('platforms') ? '25px 25px 0 0' : '25px' }}>
                                <PlatFormAccordionTitle
                                    name='platforms'
                                    expandedItems={expandedItems}
                                    title={IntlStore.intl.formatMessage({
                                        id: "NewRelease.Publish.Title",
                                        defaultMessage: "Platform and countries"
                                    })}
                                    link={`/releases/new-release/${id}?step=publish`} />
                                {
                                    expandedItems.includes('platforms')
                                        ? <BottomVector color={theme.palette.text.breadcrumbs} />
                                        : <TopVector color={theme.palette.text.breadcrumbs} />
                                }
                            </StyledAccordionItemButton>
                        </AccordionItemHeading>
                        <StyledAccordionItemPanel
                            style={{ borderRadius: expandedItems.includes('platforms') ? '0 0 25px 25px' : '25px' }}>
                            <PlatformCountries releases={releases} />
                        </StyledAccordionItemPanel>
                    </AccordionItem>
                    <AccordionItem uuid={'pricing'} key={'pricing'} id="pricing" >
                        <AccordionItemHeading >
                            <StyledAccordionItemButton
                                style={{ borderRadius: expandedItems.includes('pricing') ? '25px 25px 0 0' : '25px' }}>
                                <PlatFormAccordionTitle
                                    name=''
                                    expandedItems={expandedItems}
                                    title={IntlStore.intl.formatMessage({
                                        id: "NewRelease.Platform.Final",
                                        defaultMessage: "Final pricing"
                                    })}
                                    link=''
                                    // Don't duplicate "Paid plan" label in header; keep a single title.
                                    text={undefined}
                                />
                                {expandedItems.includes('pricing')
                                    ? <BottomVector color={theme.palette.text.breadcrumbs} />
                                    : <TopVector color={theme.palette.text.breadcrumbs} />}
                            </StyledAccordionItemButton>
                        </AccordionItemHeading>
                        <StyledAccordionItemPanel
                            style={{ borderRadius: expandedItems.includes('pricing') ? '0 0 25px 25px' : '25px' }}>
                            <FinalPricing
                                hasUgc={hasUgc}
                                step={step}
                                setStep={setStep}
                                releases={releases}
                                link={`/releases/new-release/${id}?step=type`}
                                releaseType={releaseType}
                                tracksLength={tracks?.length}
                                paid={paid}
                                deliveryId={deliveryId}
                                setDeliveryId={setDeliveryId}
                            />
                        </StyledAccordionItemPanel>
                    </AccordionItem>
                </AccordionWrapper>
            </Accordion >
            {!isAlreadySubmitted && (
            <AccordionSection>
                <ReleaseNoticeModal
                    deliveryId={deliveryId}
                    onPublish={handlePublish}
                    step={step}
                    setStep={setStep}
                        releaseDate={releases.releases.data?.release_date}
                    />
            </AccordionSection>
            )}
        </>
    );
};

const ReleaseInfoHeader = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const ReviewNavRow = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 8px;
            margin-bottom: 5px;

    }
`;

const TrackButtonLeft = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
`;

const TrackInfoHeaderRow = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    min-width: 0;
`;

const TrackInfoTitleAndEdit = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    margin-top: 10px;
`;

const TrackInfoTitle = styled.div`
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 18px;
    line-height: 25px;
    letter-spacing: 0;
    min-width: 0;
`;

const TrackInfoEditButton = styled.button`
    width: 51px;
    height: 24px;
    border-radius: 100px;
    border: none;
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 13px;
    line-height: 18px;
    letter-spacing: 0;
    cursor: pointer;
    flex: 0 0 auto;
`;

const BackLink = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    width: fit-content;
`;

const BackText = styled.span`
    font-family: SF Pro Display;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: rgba(0, 0, 0, 0.55);
`;

const ReviewTitle = styled(Typography)`
    margin: 0;
`;

const ReleaseInfoRow = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const ReleaseInfoTitleAndEdit = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: nowrap;
    min-width: 0;
`;

const ReleaseInfoTitleText = styled.span`
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 18px;
    line-height: 25px;
    letter-spacing: 0;
    vertical-align: middle;
    color: #000000;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ReleaseInfoEditButton = styled.button`
    width: 51px;
    height: 24px;
    border-radius: 100px;
    border:none;
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 13px;
    line-height: 18px;
    letter-spacing: 0;
    cursor: pointer;
`;

const AccordionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
`;
const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;
const StyledAccordionItemButton = styled(AccordionItemButton)`
    display: flex;
    gap: 18px;
    align-items: center;
    background-color: #FFFFFF;
    color: #000000;
    font-size: 18px;
    padding: 20px;
    border: none;
    cursor: pointer;
    transition: background-color 0.3s ease;
`;

const StyledAccordionItemPanel = styled(AccordionItemPanel)`
    padding: 0 20px 25px 20px;
    background-color: #FFFFFF;
    font-size: 16px;
    color: #333;
`;

const AccordionSection = styled.div`
    width: 100%;
    border-radius: 25px;
    background: #ffffff;
    position: relative;
    background-color:#fff;
`;

const Line = styled.div`
    width: calc(100% - 36px);
    height: 1px;
    background-color: #00000033;
`;

const LineBox = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

